// An HttpErrorResponse is matched by shape: the public app's interceptor re-throws a spread copy,
// which is a plain object that fails instanceof.
export interface HttpErrorLike {
  readonly name: 'HttpErrorResponse';
  readonly status: number;
  readonly message?: string;
  readonly url?: string | null;
  readonly urlAtError?: string;
}

const EXPECTED_HTTP_STATUSES: readonly number[] = [0, 401, 403, 404];
const PROFILE_TYPE_SEGMENT = /^(individual|team|office|network)$/i;
const CODE_PREFIX_SEGMENT = /^(code|stub)-/i;
const NUMERIC_SEGMENT = /^\d+$/;
const GUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// The digit requirement keeps a plain word that follows a profile type (`Team/Search`) from being read as a code.
const PROFILE_CODE_SEGMENT = /^(?=[a-z0-9]*\d)[a-z0-9]{3,12}$/i;
const ORIGIN_PREFIX = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i;
const UNKNOWN_PATH = '(unknown url)';

export function isHttpErrorResponse(value: unknown): value is HttpErrorLike {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  // SAFETY: only reads `name` and `status`, and checks both before trusting them.
  const candidate = value as { name?: unknown; status?: unknown };

  return candidate.name === 'HttpErrorResponse' && Object.hasOwn(value, 'status') && typeof candidate.status === 'number';
}

// eslint-disable-next-line no-restricted-syntax -- the caller holds whatever was thrown
export function isExpectedHttpFailure(value: unknown): boolean {
  return isHttpErrorResponse(value) && EXPECTED_HTTP_STATUSES.includes(value.status);
}

export function stripQueryAndFragment(url: string): string {
  const end = url.search(/[?#]/);

  return end === -1 ? url : url.slice(0, end);
}

export function templateHttpPath(url: string | null | undefined): string {
  if (!url) {
    return UNKNOWN_PATH;
  }

  const path = stripQueryAndFragment(url).replace(ORIGIN_PREFIX, '');
  let previous = '';

  const segments = path.split('/').map((segment) => {
    const isCode =
      CODE_PREFIX_SEGMENT.test(segment) ||
      GUID_SEGMENT.test(segment) ||
      NUMERIC_SEGMENT.test(segment) ||
      (PROFILE_TYPE_SEGMENT.test(previous) && PROFILE_CODE_SEGMENT.test(segment));

    previous = segment;

    return isCode ? '{code}' : segment;
  });

  return segments.join('/') || '/';
}

// Replaces the exact known URLs rather than pattern-matching the message, whose layout is Angular's.
function scrubMessage(message: string, urls: readonly (string | null | undefined)[]): string {
  return urls.reduce<string>((scrubbed, url) => (url ? scrubbed.split(url).join(stripQueryAndFragment(url)) : scrubbed), message);
}

// A shallow copy safe to send; the caller's object is left as it was.
export function scrubHttpError(error: HttpErrorLike): HttpErrorLike {
  return {
    ...error,
    ...(typeof error.message === 'string' && { message: scrubMessage(error.message, [error.url, error.urlAtError]) }),
    ...(typeof error.url === 'string' && { url: stripQueryAndFragment(error.url) }),
    ...(typeof error.urlAtError === 'string' && { urlAtError: stripQueryAndFragment(error.urlAtError) }),
  };
}

// The request method is left out on purpose, so two methods on one path with one status share an issue.
export function httpGroupingProperties(error: HttpErrorLike): { $exception_fingerprint: string; $issue_name: string } {
  const path = templateHttpPath(error.url);

  return {
    $exception_fingerprint: `http:${error.status}:${path}`,
    $issue_name: `HTTP ${error.status} ${path}`,
  };
}

// The scrubbed exception and merged grouping properties for an HTTP error, or undefined for any other value.
export function httpExceptionForCapture(
  error: unknown,
  properties?: Record<string, unknown>,
): { exception: HttpErrorLike; properties: Record<string, unknown> } | undefined {
  if (!isHttpErrorResponse(error)) {
    return undefined;
  }

  return { exception: scrubHttpError(error), properties: { ...properties, ...httpGroupingProperties(error) } };
}
